/**
 * Gem material for hd_neo — ported from hd_conf's createGemMaterial.tsx.
 *
 * Uses MeshRefractionMaterial (custom shader from @react-three/drei) +
 * three-mesh-bvh for BVH-based ray-traced refraction.
 *
 * Key difference from hd_conf: we use a singleton lazy-loaded instance
 * (no per-ring BVH management needed since hd_neo has a single ring).
 */

import { MeshRefractionMaterial } from '@react-three/drei/materials/MeshRefractionMaterial';
import { BufferGeometry, Camera, Color, EquirectangularReflectionMapping, LinearSRGBColorSpace, PerspectiveCamera, RepeatWrapping, ShaderMaterial, Vector2 } from 'three';
import { MeshBVH, SAH } from 'three-mesh-bvh';
import { RGBELoader } from 'three-stdlib';

// ─── Environment texture ───────────────────────────────────────────────────────

/* Dark high-contrast studio HDR (near-black surround + a few bright softboxes), the same gem
 * envmap as hd_conf. The refraction shader samples this as an equirect, so facets that catch a
 * light flash bright (>1, HDR) while facets facing the dark surround go near-black — that contrast
 * is the gem's brilliance. Loaded linear (RGBELoader) so the >1 values survive. This is the gem
 * refraction envmap only; the scene <Environment> keeps its own envmap.hdr. */
const gemEnvTexture = new RGBELoader().load('/3d/hdri/gem_studio.hdr', (texture) => {
    texture.mapping = EquirectangularReflectionMapping;
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.flipY = false;
    texture.generateMipmaps = false;
    texture.colorSpace = LinearSRGBColorSpace;
    texture.name = 'Gem Environment Texture';
});

// ─── BVH settings (matching hd_conf) ─────────────────────────────────────────

const bvhParams = {
    strategy: SAH,
    maxDepth: 64,
    maxLeafTris: 10,
    verbose: false,
    lazyGeneration: false,
};

// ─── Shader edits (matching hd_conf) ──────────────────────────────────────────

/** Default dark-mode glow intensity for the gem emissive (tunable via DebugControls). */
export const DEFAULT_GEM_EMISSIVE_STRENGTH = 0.75;

/** Fallback glow colour until GemsMesh seeds the store `glowColor` (matches GLOW_COLORS[0], cyan). */
const DEFAULT_GEM_GLOW_COLOR = 0x00f5ff;

/**
 * Applies hd_conf's fragment-shader patches to MeshRefractionMaterial, plus the
 * dark-mode gem emissive:
 *  - Tighten the 0.001 epsilon to 0.0001 (our objects are smaller than usual)
 *  - Soften Fresnel falloff exponent (10 → 1) for small gem scale
 *  - Declare the dark-mode uniforms — drei's `shaderMaterial` factory does NOT
 *    auto-declare uniforms, so a new uniform must be written into the GLSL too.
 *  - In dark mode, ADD the selected glow colour on top of the refraction output
 *    (`u_glowColor * u_emissiveStrength * u_isDark` — the store `glowColor`, shared
 *    with the metal patterns) so the gem emits from inside the setting and feeds the
 *    Bloom post-process. Adding (not mixing) keeps the refraction sparkle intact;
 *    `toneMapped:false` lets values >1 reach Bloom.
 *
 * NOTE: upstream drei now writes `mix(diffuseColor.rgb, …, diffuseColor.a)` (it
 * used to be `finalColor`/`1.0`), so the previous gl_FragColor patch had silently
 * become a no-op against the installed version. This targets the current line and
 * warns if a future drei update breaks the anchor again.
 */
function patchFragShader(diamMat: ShaderMaterial): string {
    const patched = diamMat.fragmentShader
        .split('0.001').join('0.0001')
        .replace(
            'return pow( 1.0 + dot( viewDirection, worldNormal), 10.0 );',
            'return pow( 1.0 + dot( viewDirection, worldNormal), 1.0 );',
        )
        .replace(
            'uniform vec3 color;',
            'uniform vec3 color;\n  uniform vec3 u_glowColor;\n  uniform float u_isDark;\n  uniform float u_emissiveStrength;',
        )
        .replace(
            'gl_FragColor = vec4(mix(diffuseColor.rgb, vec3(1.0), nFresnel), diffuseColor.a);',
            'gl_FragColor = vec4(mix(diffuseColor.rgb, vec3(1.0), nFresnel), diffuseColor.a);\n    gl_FragColor.rgb += u_glowColor * u_emissiveStrength * u_isDark;',
        );

    if (!patched.includes('gl_FragColor.rgb += u_glowColor')) {
        console.warn(
            '[createGemMaterial] dark-mode emissive patch did not apply — the drei ' +
            'MeshRefractionMaterial fragment shader may have changed. Update patchFragShader().',
        );
    }
    return patched;
}

// ─── Uniforms / defines helper (matching hd_conf's applyDefaultDiamMatParams) ──

function buildMaterial(color: Color): ShaderMaterial {
    const w = 1024;
    const _lodMax = Math.floor(Math.log2(w));
    const _cubeSize = Math.pow(2, _lodMax);
    const width = 3 * Math.max(_cubeSize, 16 * 7);
    const height = 4 * _cubeSize;

    const mat = new (MeshRefractionMaterial as typeof ShaderMaterial)() as ShaderMaterial;

    const uniforms = {
        aberrationStrength: { value: 0.007 },
        bounces: { value: 3 },
        envMap: { value: gemEnvTexture },
        ior: { value: 2.42 },
        fresnel: { value: 0.05 },
        resolution: { value: new Vector2(window.innerWidth, window.innerHeight) },
        fastChroma: { value: true },
        correctMips: { value: true },
        // Dark-mode gem glow: the gem emits in the selected glow colour (store
        // glowColor, shared with the metal patterns), added on top of refraction so
        // Bloom picks it up. u_isDark gates it (0 | 1); both are seeded/updated from GemsMesh.
        u_isDark: { value: 0.0 },
        u_emissiveStrength: { value: DEFAULT_GEM_EMISSIVE_STRENGTH },
        u_glowColor: { value: new Color(DEFAULT_GEM_GLOW_COLOR) },
    };

    const defines = {
        CUBEUV_MAX_MIP: `${_lodMax}.0`,
        CUBEUV_TEXEL_WIDTH: `${1.0 / width}`,
        CUBEUV_TEXEL_HEIGHT: `${1.0 / height}`,
        CHROMATIC_ABERRATIONS: uniforms.aberrationStrength.value > 0 ? '' : undefined,
        FAST_CHROMA: uniforms.fastChroma.value ? '' : undefined,
    };

    mat.setValues({
        uniforms: { ...mat.uniforms, ...uniforms },
        defines: { ...mat.defines, ...defines },
        toneMapped: false,
        transparent: false,
    });

    mat.fragmentShader = patchFragShader(mat);
    mat.uniforms.color ??= { value: color };
    mat.uniforms.color.value = color;

    return mat;
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Creates a MeshRefractionMaterial gem material and initialises its BVH
 * from the provided (merged) gem geometry.
 *
 * Call this once after all gem geometries are merged, then attach the returned
 * material to the gem mesh.
 *
 * `color` should be a linear-space Color (pass `new Color(1,1,1).multiplyScalar(2)`
 * for white diamond, matching hd_conf's gemColorMultiplier = 2).
 */
/**
 * Last created gem material — exposed so DebugControls can patch uniforms at runtime.
 * Only valid after the first `createGemMaterial` call.
 */
let _lastGemMat: ShaderMaterial | null = null;

export function getLastGemMaterial(): ShaderMaterial | null {
    return _lastGemMat;
}

export function createGemMaterial(geometry: BufferGeometry, color?: Color): ShaderMaterial {
    const c = color ?? new Color(1, 1, 1).multiplyScalar(2);
    const mat = buildMaterial(c);

    // Build BVH from the actual merged gem geometry (non-indexed copy, matching hd_conf).
    geometry.clearGroups();
    const bvh = new MeshBVH(geometry.clone().toNonIndexed(), bvhParams);
    (mat.uniforms.bvh as { value: { updateFrom: (b: MeshBVH) => void } }).value.updateFrom(bvh);
    mat.uniforms.resolution?.value?.set(window.innerWidth, window.innerHeight);
    mat.uniformsNeedUpdate = true;

    // Keep camera matrices in sync (needed for refraction ray origin).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mat as any).onBeforeRender = (_r: unknown, _s: unknown, camera: Camera, _g: unknown, _mesh: unknown) => {
        mat.uniforms.viewMatrixInverse?.value?.copy(camera.matrixWorld);
        mat.uniforms.projectionMatrixInverse?.value?.copy(
            (camera as PerspectiveCamera).projectionMatrixInverse,
        );
        mat.uniformsNeedUpdate = true;
    };

    _lastGemMat = mat;
    return mat;
}

/**
 * Imperatively set the dark/light state on a gem material — drives the emissive
 * glow (gem emits the selected glow colour in dark mode). Call on every `isDark`
 * store change.
 */
export function setGemDark(mat: ShaderMaterial, isDark: boolean): void {
    if (mat.uniforms.u_isDark) mat.uniforms.u_isDark.value = isDark ? 1.0 : 0.0;
}

/** Imperatively set the gem emissive (dark-mode glow) intensity. */
export function setGemEmissiveStrength(mat: ShaderMaterial, value: number): void {
    if (mat.uniforms.u_emissiveStrength) mat.uniforms.u_emissiveStrength.value = value;
}

/** Imperatively set the gem emissive glow colour (Three.js hex) — match the store `glowColor`. */
export function setGemGlowColor(mat: ShaderMaterial, hex: number): void {
    if (mat.uniforms.u_glowColor) mat.uniforms.u_glowColor.value.set(hex);
}
